import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";
import axios from "axios";

const AddPlanFeatures = () => {
  const { id } = useParams(); // Get ID from URL params for edit mode
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [loadingFeature, setLoadingFeature] = React.useState(false);
  const [errors, setErrors] = React.useState({});

  const [featureData, setFeatureData] = React.useState({
    name: "",
    description: "",
    feature_key: "",
    feature_type: "numeric", // Default value
    default_value: "",
    is_active: isEditMode ? "" : 1,
    feature_order: ""
  });

  // console.log(featureData.is_active, "featureData");

  const {
    name,
    description,
    feature_key,
    feature_type,
    default_value,
    is_active,
    feature_order
  } = featureData;

  // Fetch feature details for edit mode
  React.useEffect(() => {
    if (isEditMode && id) {
      fetchFeatureDetails(id);
    }
  }, [isEditMode, id]);

  const fetchFeatureDetails = async (featureId) => {
    setLoadingFeature(true);
    try {
      const response = await fetch(`${API_BASE_URL}/get-features-details/${featureId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add authorization headers if needed
          'Authorization': `Bearer ${localStorage.getItem("cms_token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      // console.log(result, "result");
      
      if (result.status === "success") {
        setFeatureData({
          name: result.data.name || "",
          description: result.data.description || "",
          feature_key: result.data.feature_key || "",
          feature_type: result.data.feature_type || "numeric",
          default_value: result.data.default_value || "",
          is_active: result.data.is_active !== undefined ? result.data.is_active : 1,
          feature_order: result.data.feature_order || "",
        });
      } else {
        console.error('Failed to fetch feature details:', result.message);
        // Optionally show error toast/notification
      }
    } catch (error) {
      console.error('Error fetching feature details:', error);
      // Optionally show error toast/notification
    } finally {
      setLoadingFeature(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = "Feature Name is required";
    }
    
    if (!description.trim()) {
      newErrors.description = "Description is required";
    }
    
    if (!feature_key.trim()) {
      newErrors.feature_key = "Feature Key is required";
    } else if (!/^[a-zA-Z0-9_]+$/.test(feature_key)) {
      newErrors.feature_key = "Feature Key can only contain letters, numbers, and underscores";
    }
    
    if (!feature_type) {
      newErrors.feature_type = "Feature Type is required";
    }
    
    if (!default_value.toString().trim()) {
      newErrors.default_value = "Default Value is required";
    } else {
      // Validate based on feature type
      if (feature_type === "numeric" && isNaN(Number(default_value))) {
        newErrors.default_value = "Default Value must be a valid number for numeric type";
      }
      if (feature_type === "boolean" && !["true", "false"].includes(default_value)) {
        newErrors.default_value = "Default Value must be true or false for boolean type";
      }
    }

    if (!feature_order) {
      newErrors.feature_order = "Feature Order is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }

    setFeatureData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Reset default_value when feature_type changes
    if (name === "feature_type") {
      setFeatureData((prevData) => ({
        ...prevData,
        feature_type: value,
        default_value: value === "boolean" ? "true" : "",
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);
    
    try {
      const apiUrl = isEditMode 
        ? `${API_BASE_URL}/update-features/${id}` 
        : `${API_BASE_URL}/add-features`;
      
      const payload = {
        name: name.trim(),
        description: description.trim(),
        feature_key: feature_key.trim(),
        feature_type,
        default_value: default_value,
        feature_order: feature_order,
        is_active: Number(is_active),
      };

      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
        },
      });

      const result = response.data;
      
      if (result.status === "success") {
        // Success - redirect to features list
        navigate('/cms/manage-subscription-features');
        toast.success(isEditMode ? 'Feature updated successfully!' : 'Feature added successfully!');
      } else {
        // Handle API error response
        console.error('API Error:', result.message);
        // Optionally show error toast/notification
        
        // If there are field-specific errors from API
        if (result.errors) {
          setErrors(result.errors);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Optionally show error toast/notification
    } finally {
      setSubmitLoading(false);
    }
  };

  // Show loading state while fetching feature details
  if (isEditMode && loadingFeature) {
    return (
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2">Loading feature details...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0 font-size-18">
                    {isEditMode ? "Edit Feature" : "Add Feature"}
                  </h4>
                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item">
                        <a href="javascript: void(0);">CMS</a>
                      </li>
                      <li className="breadcrumb-item">
                        <Link to={"/cms/manage-subscription-features"}>
                          Manage Features
                        </Link>
                      </li>
                      <li className="breadcrumb-item active">
                        {isEditMode ? "Edit Feature" : "Add Feature"}
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-12">
              <div className="row">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="card-title mb-4">
                        {isEditMode ? "Edit Feature" : "Add Feature"}
                      </h4>
                      <form onSubmit={handleSubmit}>
                        <div data-repeater-list="outer-group" className="outer">
                          <div data-repeater-item className="outer row">
                            <div className="col-md-6 col-xl-9">
                              {/* Feature Name */}
                              <div className="form-group row mb-4">
                                <label
                                  htmlFor="taskname"
                                  className="col-form-label col-lg-2"
                                >
                                  Feature Name <span className="text-danger">*</span>
                                </label>
                                <div className="col-lg-10">
                                  <input
                                    id="taskname"
                                    name="name"
                                    type="text"
                                    value={name}
                                    className={`form-control ${
                                      errors.name ? "is-invalid" : ""
                                    }`}
                                    placeholder="Enter Feature Name..."
                                    onChange={handleInputChange}
                                    maxLength="255"
                                  />
                                  {errors.name && (
                                    <div className="invalid-feedback">
                                      {errors.name}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Description */}
                              <div className="form-group row mb-4">
                                <label
                                  htmlFor="description"
                                  className="col-form-label col-lg-2"
                                >
                                  Description <span className="text-danger">*</span>
                                </label>
                                <div className="col-lg-10">
                                  <textarea
                                    id="description"
                                    name="description"
                                    value={description}
                                    className={`form-control ${
                                      errors.description ? "is-invalid" : ""
                                    }`}
                                    placeholder="Enter Description..."
                                    onChange={handleInputChange}
                                    rows="3"
                                    maxLength="500"
                                  />
                                  {errors.description && (
                                    <div className="invalid-feedback">
                                      {errors.description}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Feature Key */}
                              <div className="form-group row mb-4">
                                <label
                                  htmlFor="feature_key"
                                  className="col-form-label col-lg-2"
                                >
                                  Feature Key <span className="text-danger">*</span>
                                </label>
                                <div className="col-lg-10">
                                  <input
                                    id="feature_key"
                                    name="feature_key"
                                    type="text"
                                    value={feature_key}
                                    className={`form-control ${
                                      errors.feature_key ? "is-invalid" : ""
                                    }`}
                                    placeholder="Enter Feature Key (e.g., max_users)..."
                                    onChange={handleInputChange}
                                    maxLength="100"
                                  />
                                  {errors.feature_key && (
                                    <div className="invalid-feedback">
                                      {errors.feature_key}
                                    </div>
                                  )}
                                  <small className="form-text text-muted">
                                    Used for API integration. Only letters, numbers, and underscores allowed.
                                  </small>
                                </div>
                              </div>

                              {/* Feature Type */}
                              <div className="form-group row mb-4">
                                <label
                                  htmlFor="feature_type"
                                  className="col-form-label col-lg-2"
                                >
                                  Feature Type <span className="text-danger">*</span>
                                </label>
                                <div className="col-lg-10">
                                  <select
                                    name="feature_type"
                                    id="feature_type"
                                    onChange={handleInputChange}
                                    value={feature_type}
                                    className={`form-select ${
                                      errors.feature_type ? "is-invalid" : ""
                                    }`}
                                  >
                                    <option value="">Select Feature Type</option>
                                    <option value="numeric">Numeric</option>
                                    <option value="text">Text</option>
                                    <option value="boolean">Boolean</option>
                                  </select>
                                  {errors.feature_type && (
                                    <div className="invalid-feedback">
                                      {errors.feature_type}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Default Value */}
                              <div className="form-group row mb-4">
                                <label
                                  htmlFor="default_value"
                                  className="col-form-label col-lg-2"
                                >
                                  Default Value <span className="text-danger">*</span>
                                </label>
                                <div className="col-lg-10">
                                  {feature_type === "boolean" ? (
                                    <select
                                      name="default_value"
                                      id="default_value"
                                      onChange={handleInputChange}
                                      value={default_value}
                                      className={`form-select ${
                                        errors.default_value ? "is-invalid" : ""
                                      }`}
                                    >
                                      <option value="true">True</option>
                                      <option value="false">False</option>
                                    </select>
                                  ) : (
                                    <input
                                      id="default_value"
                                      name="default_value"
                                      type="text"
                                      value={default_value}
                                      className={`form-control ${
                                        errors.default_value ? "is-invalid" : ""
                                      }`}
                                      placeholder={"Enter default value..."}
                                      onChange={handleInputChange}
                                      // min={feature_type === "numeric" ? 0 : undefined}
                                      // step={feature_type === "numeric" ? "any" : undefined}
                                      // maxLength={feature_type === "text" ? "255" : undefined}
                                    />
                                  )}
                                  {errors.default_value && (
                                    <div className="invalid-feedback">
                                      {errors.default_value}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Feature Order */}
                                <div className="form-group row mb-4">
                                <label
                                  htmlFor="taskname"
                                  className="col-form-label col-lg-2"
                                >
                                  Feature Order <span className="text-danger">*</span>
                                </label>
                                <div className="col-lg-10">
                                  <input
                                    id="taskname"
                                    name="feature_order"
                                    type="text"
                                    value={feature_order}
                                    className={`form-control ${
                                      errors.feature_order ? "is-invalid" : ""
                                    }`}
                                    placeholder="Enter Feature Order..."
                                    onChange={handleInputChange}
                                    maxLength="255"
                                  />
                                  {errors.feature_order && (
                                    <div className="invalid-feedback">
                                      {errors.feature_order}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Status */}
                              <div className="form-group row mb-4">
                                <label
                                  htmlFor="is_active"
                                  className="col-form-label col-lg-2"
                                >
                                  Status
                                </label>
                                <div className="col-lg-10">
                                  <select
                                    name="is_active"
                                    id="is_active"
                                    value={is_active}
                                    onChange={handleInputChange}
                                    className={`form-select ${
                                      errors.is_active ? "is-invalid" : ""
                                    }`}
                                  >
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                  </select>
                                  {errors.is_active && (
                                    <div className="invalid-feedback">
                                      {errors.is_active}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="row justify-content-end">
                          <div className="col-lg-10">
                            <div className="d-flex gap-2">
                              <Link
                                to="/cms/manage-subscription-features"
                                className="btn btn-secondary"
                              >
                                Cancel
                              </Link>
                              <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={submitLoading}
                              >
                                {submitLoading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Loading...
                                      </span>
                                    </span>
                                    {isEditMode ? "Updating..." : "Creating..."}
                                  </>
                                ) : isEditMode ? (
                                  "Update Feature"
                                ) : (
                                  "Add Feature"
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddPlanFeatures;